<template>
    <div class="flex flex-col gap-y-3">
        <!-- Kopfzeile: Status, Schritt, Uhr -->
        <div class="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
            <span
                v-if="playback.live.value"
                class="flex items-center gap-1.5 rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold tracking-wide text-red-700 uppercase"
            >
                <span class="relative flex h-2 w-2">
                    <span
                        class="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75"
                    />
                    <span
                        class="relative inline-flex h-2 w-2 rounded-full bg-red-600"
                    />
                </span>
                {{ t("helpers.inputs.label.vectorize_live") }}
            </span>
            <span
                v-else
                class="bg-neutral-10 text-neutral-4 rounded-full px-2 py-0.5 text-xs font-semibold tracking-wide uppercase"
            >
                {{ t("helpers.inputs.label.vectorize_replay") }}
            </span>

            <span class="font-medium">{{ phaseLabel }}</span>

            <span v-if="playback.step.value > 0" class="text-neutral-5">
                {{
                    playback.maxIterations.value > 0
                        ? t("helpers.inputs.label.vectorize_step_of", {
                              max: playback.maxIterations.value,
                              step: playback.step.value,
                          })
                        : t("helpers.inputs.label.vectorize_step", {
                              step: playback.step.value,
                          })
                }}
            </span>

            <span class="text-neutral-5 ml-auto font-mono text-xs tabular-nums">
                {{ formatClock(playback.elapsedMs.value) }}
            </span>
        </div>

        <div class="grid gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
            <!-- Bühne -->
            <div class="flex flex-col gap-y-2">
                <div
                    class="vz-stage chessboard relative h-72 w-full overflow-hidden rounded-lg border lg:h-80"
                    :class="{ 'vz-stage--upscaling': isUpscaling }"
                >
                    <div
                        class="absolute inset-2 flex items-center justify-center"
                    >
                        <div
                            class="relative h-full max-w-full"
                            :style="{ aspectRatio: frameAspect }"
                        >
                            <Transition name="vz-frame">
                                <div
                                    v-if="frame"
                                    :key="playback.currentIndex.value"
                                    class="absolute inset-0"
                                    :class="{
                                        'vz-frame--sharpen':
                                            frame.kind === 'upscaled',
                                    }"
                                >
                                    <SvgEditorDocument
                                        v-if="
                                            frame.kind === 'result' && frame.svg
                                        "
                                        :svg="frame.svg"
                                        @view-box="onDocumentBox"
                                    />
                                    <img
                                        v-else
                                        :src="frame.image"
                                        alt=""
                                        class="h-full w-full object-contain"
                                        draggable="false"
                                        @load="onFrameLoad"
                                    />
                                </div>
                            </Transition>

                            <VectorizeToolAttention
                                v-if="attentionTool"
                                :key="attentionTool.id"
                                :tool="attentionTool.tool"
                                :input="attentionTool.input"
                            />
                        </div>
                    </div>

                    <div
                        v-if="isUpscaling"
                        class="vz-scan pointer-events-none absolute inset-x-0"
                    />

                    <div
                        v-if="showThinking"
                        class="pointer-events-none absolute inset-x-0 bottom-0"
                    >
                        <div class="vz-shimmer h-1 w-full" />
                    </div>

                    <div
                        class="pointer-events-none absolute inset-x-2 bottom-2 flex items-end justify-between gap-2"
                    >
                        <span
                            class="max-w-full truncate rounded-md bg-black/60 px-2 py-1 text-xs text-white backdrop-blur-sm"
                        >
                            {{ frameCaption }}
                        </span>
                        <span
                            v-if="playback.frames.value.length > 1"
                            class="rounded-md bg-black/60 px-2 py-1 text-xs text-white tabular-nums backdrop-blur-sm"
                        >
                            {{
                                t("helpers.inputs.label.vectorize_frame_of", {
                                    count: playback.frames.value.length,
                                    index: playback.currentIndex.value + 1,
                                })
                            }}
                        </span>
                    </div>
                </div>

                <!-- Zeitleiste -->
                <div class="flex items-center gap-2">
                    <button
                        type="button"
                        class="hover:bg-neutral-9 flex h-8 w-8 items-center justify-center rounded-md border bg-white text-sm disabled:opacity-40"
                        :disabled="playback.currentIndex.value === 0"
                        :aria-label="t('helpers.buttons.previous')"
                        @click="playback.previous()"
                    >
                        <SmuIcon icon="backward-step" type="light" />
                    </button>
                    <button
                        type="button"
                        class="hover:bg-neutral-9 flex h-8 w-8 items-center justify-center rounded-md border bg-white text-sm disabled:opacity-40"
                        :disabled="playback.frames.value.length < 2"
                        :aria-label="
                            playback.playing.value
                                ? t('helpers.buttons.vectorize_pause')
                                : t('helpers.buttons.vectorize_play')
                        "
                        @click="playback.togglePlay()"
                    >
                        <SmuIcon
                            :icon="playback.playing.value ? 'pause' : 'play'"
                            type="light"
                        />
                    </button>
                    <button
                        type="button"
                        class="hover:bg-neutral-9 flex h-8 w-8 items-center justify-center rounded-md border bg-white text-sm disabled:opacity-40"
                        :disabled="
                            playback.currentIndex.value >=
                            playback.lastIndex.value
                        "
                        :aria-label="t('helpers.buttons.next')"
                        @click="playback.next()"
                    >
                        <SmuIcon icon="forward-step" type="light" />
                    </button>

                    <div class="relative mx-2 h-8 flex-1">
                        <div
                            class="bg-neutral-9 absolute top-1/2 right-0 left-0 h-1 -translate-y-1/2 rounded-full"
                        />
                        <div
                            class="bg-primary-6 absolute top-1/2 left-0 h-1 -translate-y-1/2 rounded-full transition-[width] duration-300"
                            :style="{ width: progressPercent + '%' }"
                        />
                        <button
                            v-for="(item, index) in playback.frames.value"
                            :key="index"
                            type="button"
                            class="absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow transition-transform hover:scale-125"
                            :class="
                                index <= playback.currentIndex.value
                                    ? 'bg-primary-6'
                                    : 'bg-neutral-7'
                            "
                            :style="{ left: markerPercent(index) + '%' }"
                            :aria-label="frameLabel(item)"
                            @click="playback.seek(index)"
                        />
                    </div>

                    <SmuButton
                        v-if="playback.live.value && !playback.following.value"
                        size="sm"
                        variant="outline"
                        @click="playback.backToLive()"
                    >
                        {{ t("helpers.buttons.vectorize_back_to_live") }}
                    </SmuButton>
                </div>
            </div>

            <!-- Gedanken & Werkzeuge -->
            <ol
                ref="feedRef"
                class="bg-neutral-10 flex h-72 flex-col gap-y-1.5 overflow-x-hidden overflow-y-auto rounded-lg border p-2 text-sm lg:h-[calc(20rem+2.5rem)]"
            >
                <TransitionGroup name="vz-entry">
                    <li
                        v-for="entry in playback.entries.value"
                        :key="entry.id"
                        class="rounded-md px-2 py-1 transition-opacity duration-300"
                        :class="entryClasses(entry)"
                    >
                        <template v-if="entry.kind === 'start'">
                            <SmuIcon icon="flag" type="light" class="mr-1" />
                            {{
                                entry.cached
                                    ? t("helpers.inputs.label.vectorize_cached")
                                    : t(
                                          "helpers.inputs.label.vectorize_start_with",
                                          {
                                              model: modelLabel(entry.provider),
                                          },
                                      )
                            }}
                        </template>

                        <template v-else-if="entry.kind === 'upscale'">
                            <SmuIcon
                                v-if="entry.status === 'running'"
                                icon="spinner"
                                type="light"
                                :spin-pulse="true"
                                class="mr-1"
                            />
                            <SmuIcon
                                v-else
                                :icon="
                                    entry.status === 'done'
                                        ? 'check'
                                        : 'triangle-exclamation'
                                "
                                type="light"
                                class="mr-1"
                            />
                            {{
                                entry.status === "failed"
                                    ? t(
                                          "helpers.inputs.label.vectorize_upscale_failed",
                                      )
                                    : entry.status === "done"
                                      ? t(
                                            "helpers.inputs.label.vectorize_upscaled",
                                        )
                                      : t(
                                            "helpers.inputs.label.vectorize_phase_upscale",
                                        )
                            }}
                        </template>

                        <template v-else-if="entry.kind === 'step'">
                            <span
                                class="text-xs font-semibold tracking-wide uppercase"
                            >
                                {{
                                    t("helpers.inputs.label.vectorize_step", {
                                        step: entry.step,
                                    })
                                }}
                            </span>
                        </template>

                        <template v-else-if="entry.kind === 'thought'">
                            <div class="flex gap-2">
                                <SmuIcon
                                    icon="brain"
                                    type="light"
                                    class="text-neutral-5 mt-0.5 shrink-0"
                                />
                                <div class="min-w-0">
                                    <div
                                        v-if="thoughtParts(entry.text).title"
                                        class="font-medium"
                                    >
                                        {{ thoughtParts(entry.text).title }}
                                    </div>
                                    <div
                                        class="text-neutral-4 whitespace-pre-wrap italic"
                                    >
                                        {{ thoughtParts(entry.text).body }}
                                    </div>
                                </div>
                            </div>
                        </template>

                        <template v-else-if="entry.kind === 'tool'">
                            <button
                                type="button"
                                class="flex w-full cursor-pointer gap-2 text-left"
                                :disabled="entry.frameIndex === undefined"
                                @click="
                                    entry.frameIndex !== undefined &&
                                    playback.seek(entry.frameIndex)
                                "
                            >
                                <SmuIcon
                                    v-if="entry.status === 'running'"
                                    icon="spinner"
                                    type="light"
                                    :spin-pulse="true"
                                    class="mt-0.5 shrink-0"
                                />
                                <SmuIcon
                                    v-else
                                    :icon="
                                        entry.status === 'done'
                                            ? 'wrench'
                                            : 'triangle-exclamation'
                                    "
                                    type="light"
                                    class="mt-0.5 shrink-0"
                                    :class="
                                        entry.status === 'failed'
                                            ? 'text-red-600'
                                            : 'text-neutral-5'
                                    "
                                />
                                <span class="min-w-0 flex-1">
                                    <span class="font-medium">
                                        {{ toolLabel(entry.tool) }}
                                    </span>
                                    <span
                                        v-if="entry.status === 'running'"
                                        class="text-neutral-5 ml-1"
                                    >
                                        {{
                                            t(
                                                "helpers.inputs.label.vectorize_tool_running",
                                            )
                                        }}
                                    </span>
                                    <span
                                        v-else-if="
                                            entry.durationMs !== undefined
                                        "
                                        class="text-neutral-5 ml-1 text-xs tabular-nums"
                                    >
                                        {{
                                            (entry.durationMs / 1000).toFixed(
                                                1,
                                            )
                                        }}s
                                    </span>
                                    <span
                                        v-if="
                                            Object.keys(entry.input).length > 0
                                        "
                                        class="mt-0.5 flex flex-wrap gap-1"
                                    >
                                        <span
                                            v-for="(value, key) in entry.input"
                                            :key="key"
                                            class="bg-neutral-10 text-neutral-4 rounded px-1 py-0.5 font-mono text-[11px]"
                                        >
                                            {{ key }}: {{ formatValue(value) }}
                                        </span>
                                    </span>
                                    <span
                                        v-if="entry.summary"
                                        class="text-neutral-5 mt-0.5 block text-xs [overflow-wrap:anywhere] whitespace-pre-wrap"
                                    >
                                        {{ shortSummary(entry.summary) }}
                                    </span>
                                    <span
                                        v-if="entry.message"
                                        class="mt-0.5 block text-xs text-red-600"
                                    >
                                        {{ entry.message }}
                                    </span>
                                </span>
                            </button>
                        </template>

                        <template v-else-if="entry.kind === 'result'">
                            <div class="flex gap-2">
                                <SmuIcon
                                    icon="check"
                                    type="light"
                                    class="mt-0.5 shrink-0 text-green-700"
                                />
                                <div class="min-w-0">
                                    <div class="font-medium">
                                        {{ entry.summary }}
                                    </div>
                                    <ul
                                        v-if="entry.changes.length > 0"
                                        class="text-neutral-4 mt-0.5 list-disc pl-4 text-xs"
                                    >
                                        <li
                                            v-for="change in entry.changes"
                                            :key="change"
                                        >
                                            {{ change }}
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </template>

                        <template v-else-if="entry.kind === 'error'">
                            <SmuIcon
                                icon="triangle-exclamation"
                                type="light"
                                class="mr-1"
                            />
                            {{ entry.message }}
                        </template>
                    </li>
                </TransitionGroup>

                <li
                    v-if="showThinking"
                    class="text-neutral-5 flex items-center gap-2 px-2 py-1"
                >
                    <SmuIcon icon="spinner" type="light" :spin-pulse="true" />
                    {{ t("helpers.inputs.label.vectorize_thinking") }}
                </li>
            </ol>
        </div>
    </div>
</template>

<script setup lang="ts">
import { SmuButton, SmuIcon } from "@smakecloud/smake-ui";
import { computed, nextTick, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import type { VectorizePlayback } from "~/composables/useVectorizePlayback";
import type { Box } from "~/lib/svgEditor/selection";
import type { PlaybackEntry, PlaybackFrame } from "~/lib/vectorize/playback";

const props = defineProps<{
    playback: VectorizePlayback;
}>();

const { t } = useI18n();

const feedRef = ref<HTMLOListElement>();

const frame = computed(() => props.playback.currentFrame.value);

// Seitenverhältnis des gezeigten Bildes — die Bühne legt darüber einen
// gleich großen Kasten, damit Bereichs-Markierungen in Bildanteilen passen.
const frameAspect = ref<number>(4 / 3);

function onAspect(ratio: number) {
    frameAspect.value = ratio;
}

function onDocumentBox(box: Box) {
    onAspect(box.width / box.height);
}

function onFrameLoad(event: Event) {
    const image = event.target as HTMLImageElement;

    if (image.naturalWidth > 0 && image.naturalHeight > 0) {
        onAspect(image.naturalWidth / image.naturalHeight);
    }
}

// Solange live mitgeschaut wird, zeigt die Bühne, woran das laufende
// Werkzeug gerade arbeitet.
const attentionTool = computed(() => {
    const entry = props.playback.runningTool.value;

    if (entry?.kind !== "tool" || !props.playback.following.value) {
        return;
    }

    return entry;
});

const isUpscaling = computed(
    () =>
        props.playback.phase.value === "upscaling" &&
        props.playback.following.value,
);

// Zwischen zwei Werkzeugaufrufen "denkt" die KI — dann läuft ein Schimmer.
const showThinking = computed(
    () =>
        props.playback.phase.value === "vectorizing" &&
        props.playback.runningTool.value === undefined,
);

const phaseLabel = computed(() => {
    switch (props.playback.phase.value) {
        case "done":
            return t("helpers.inputs.label.vectorize_phase_done");

        case "error":
            return t("helpers.inputs.label.vectorize_phase_error");

        case "upscaling":
            return t("helpers.inputs.label.vectorize_phase_upscale");

        case "vectorizing":
            return props.playback.mode.value === "edit"
                ? t("helpers.inputs.label.vectorize_phase_edit")
                : t("helpers.inputs.label.vectorize_phase_vectorize");

        default:
            return "";
    }
});

const frameCaption = computed(() =>
    frame.value ? frameLabel(frame.value) : "",
);

const progressPercent = computed(() =>
    props.playback.lastIndex.value === 0
        ? 0
        : (props.playback.currentIndex.value / props.playback.lastIndex.value) *
          100,
);

// Beim Scrubben werden Einträge, die nach dem gezeigten Bild passierten,
// abgeblendet — so bleibt sichtbar, was zum aktuellen Bild gehört.
function entryClasses(entry: PlaybackEntry): string[] {
    const classes: string[] = [];
    const current = frame.value;

    if (current && !props.playback.following.value && entry.at > current.at) {
        classes.push("opacity-40");
    }

    if (entry.kind === "step") classes.push("mt-1 text-neutral-5");
    if (entry.kind === "error") classes.push("bg-red-50 text-red-700");
    if (entry.kind === "result") classes.push("bg-green-50");

    return classes;
}

function formatClock(ms: number): string {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

// Manche Werkzeuge (z. B. inspectShapes) berichten dem Modell seitenlange
// Listen — für den Menschen reicht der Anfang.
const MAX_SUMMARY_LENGTH = 320;

function formatValue(value: unknown): string {
    if (typeof value === "string") return value;

    if (typeof value === "number" || typeof value === "boolean") {
        return String(value);
    }

    try {
        return JSON.stringify(value);
    } catch {
        return "";
    }
}

function frameLabel(item: PlaybackFrame): string {
    switch (item.kind) {
        case "result":
            return t("helpers.inputs.label.vectorize_result");

        case "source":
            return t("helpers.inputs.label.vectorize_source_preview");

        case "tool":
            return item.tool ? toolLabel(item.tool) : "";

        case "upscaled":
            return t("helpers.inputs.label.vectorize_upscaled");

        default:
            return "";
    }
}

function markerPercent(index: number): number {
    return props.playback.lastIndex.value === 0
        ? 0
        : (index / props.playback.lastIndex.value) * 100;
}

function modelLabel(provider: string): string {
    const key = `helpers.inputs.label.vectorize_model_${provider.replaceAll("-", "_")}`;

    return translated(key) ?? provider;
}

function shortSummary(summary: string): string {
    const trimmed = summary.trim();

    return trimmed.length > MAX_SUMMARY_LENGTH
        ? `${trimmed.slice(0, MAX_SUMMARY_LENGTH).trimEnd()} …`
        : trimmed;
}

/**
 * Die Denk-Zusammenfassungen der Modelle beginnen oft mit einem fett
 * gesetzten Titel (`**Titel**`); der wird als Überschrift gezeigt.
 */
function thoughtParts(text: string): { body: string; title?: string } {
    const match = /^\s*\*\*(?<title>[^*]+)\*\*\s*/.exec(text);

    if (!match?.groups) return { body: text.replaceAll("**", "") };

    return {
        body: text.slice(match[0].length).replaceAll("**", ""),
        title: match.groups.title.trim(),
    };
}

function toolLabel(tool: string): string {
    const key = `helpers.inputs.label.vectorize_tool_${tool}`;

    return translated(key) ?? tool;
}

// Die Übersetzungen sind flache Schlüssel mit Punkten; `te()` würde sie als
// verschachtelte Pfade lesen und nie finden. `t()` gibt bei einem unbekannten
// Schlüssel den Schlüssel selbst zurück.
function translated(key: string): string | undefined {
    const value = t(key);

    return value === key ? undefined : value;
}

// Live mitlaufen: neue Einträge ins Bild scrollen, solange nicht gescrubbt wird.
watch(
    () => [
        props.playback.entries.value.length,
        props.playback.entries.value.at(-1)?.kind === "thought"
            ? (props.playback.entries.value.at(-1) as { text: string }).text
                  .length
            : 0,
    ],
    () => {
        if (!props.playback.following.value) return;

        void nextTick(() => {
            feedRef.value?.scrollTo({
                behavior: "smooth",
                top: feedRef.value.scrollHeight,
            });
        });
    },
);
</script>

<style scoped>
.vz-frame-enter-active,
.vz-frame-leave-active {
    transition:
        opacity 450ms ease,
        transform 450ms ease;
}

.vz-frame-enter-from {
    opacity: 0;
    transform: scale(1.03);
}

.vz-frame-leave-to {
    opacity: 0;
}

/* Hochskalieren: die Vorlage "atmet" unscharf, ein Lichtband tastet sie ab. */
.vz-stage--upscaling img {
    animation: vz-focus 1.8s ease-in-out infinite;
}

@keyframes vz-focus {
    0%,
    100% {
        filter: blur(3px) saturate(0.7);
    }

    50% {
        filter: blur(1px) saturate(0.9);
    }
}

.vz-scan {
    height: 22%;
    background: linear-gradient(
        to bottom,
        transparent,
        rgb(255 255 255 / 0.55) 45%,
        var(--color-primary-100, #9ccc16) 50%,
        rgb(255 255 255 / 0.55) 55%,
        transparent
    );
    opacity: 0.8;
    mix-blend-mode: screen;
    animation: vz-scan 1.6s cubic-bezier(0.45, 0, 0.55, 1) infinite;
}

@keyframes vz-scan {
    0% {
        top: -25%;
    }

    100% {
        top: 105%;
    }
}

/* Das hochskalierte Bild rastet scharf ein. */
.vz-frame--sharpen {
    animation: vz-sharpen 700ms ease-out;
}

@keyframes vz-sharpen {
    0% {
        filter: blur(6px);
        transform: scale(1.04);
    }

    100% {
        filter: blur(0);
        transform: scale(1);
    }
}

.vz-shimmer {
    background: linear-gradient(
        90deg,
        transparent 0%,
        var(--color-primary-100, #9ccc16) 50%,
        transparent 100%
    );
    background-size: 50% 100%;
    animation: vz-shimmer 1.4s linear infinite;
}

@keyframes vz-shimmer {
    0% {
        background-position: -100% 0;
    }

    100% {
        background-position: 200% 0;
    }
}

.vz-entry-enter-active {
    transition:
        opacity 300ms ease,
        transform 300ms ease;
}

.vz-entry-enter-from {
    opacity: 0;
    transform: translateY(4px);
}
</style>
